import { html, LitElement, TemplateResult } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { IPagoInfoFacturarConceptoComplemento as IComplementoIEDU } from "../../../../data/entidad/Factura";
import _L from "../../../../util/Labels";
import '../../../components/Icon';
import { FormGenerator } from "../../../controlD3/Formulario";
import { _ModalForm, IModalFormController } from "../../../controlD3/ModalThings/ModalForm";
import { NotificacionV2 } from "../../../controlD3/NotificacionV2";
import { HTMLTooltipComponent, TooltipElementObservedConfig } from "../../../controlWC/TooltipComponent";
import { UIUtilGeneral } from "../../../util/Util";
import { AlumnoEdoCtaFacturacionUtil as util } from './FacturacionUtil';
import './FacturacionViewCFDIInfoComplemento.scss';
import ic_close from '/image/iconos/ic_close.svg?raw';

export namespace UIUtilViewAlumnoEdoCtaFacturacionViewCFDIInfoComplemento {

    type TReglasComplementoIEDU = {
        [k in keyof IComplementoIEDU]?: {
            required: boolean
            minlength?: number
            maxlength?: number
            fnExtraValide?: (value: string) => boolean
        }
    }
    const REGLAS_COMPLEMENTO_IEDU: TReglasComplementoIEDU = {
        NombreAlumno: { required: true },
        CURP: { required: true, minlength: 18, maxlength: 19, fnExtraValide: (value) => UIUtilGeneral._ValidarCURP(value) },
        NivelEducativo: { required: true },
        AutRVOE: { required: true },
        RFCPago: { required: false, minlength: 11, maxlength: 13 },
    };

    // ****************************************************************************************
    // MAIN COMPONENT
    // ****************************************************************************************

    @customElement('app-factura-complemento-concepto')
    export class FacturaComplementoConceptoComponent extends LitElement {
        private form: FormComplemento;
        private modalForm: ModalController;

        @property({ type: Number, reflect: true }) _idAlumno: number = 0;
        @property({ type: Object, reflect: false }) _dataComplementoIEDU: IComplementoIEDU = null;
        @property({ type: Boolean, reflect: true }) _required: boolean = false;
        @property({ type: Boolean, reflect: true }) private _isDataValid: boolean = false;
        @property({ type: Boolean, reflect: true }) _hasError: boolean = false;

        @query('wc-tooltip') tooltipElement: HTMLTooltipComponent;
        @query('.complemento-iedu-content') complementoContentElement: HTMLElement;
        @query('.btn-close') btnCloseElement: HTMLElement;
        // static styles = css``;

        protected createRenderRoot(): HTMLElement | DocumentFragment {
            return this; // desactivar shadow DOM
        }

        protected render(): TemplateResult {
            return html`
            <div
                class="complemento-iedu-content ${this._hasError ? 'error' : ''}"
                @click=${this.OpenComplementoIEDUModalForm}>
                ${this._dataComplementoIEDU
                    ? _L("panelfinanzasedocuenta_factura.ins_educ")
                    : _L("panelfinanzasedocuenta_factura.sin_complemento")
                }
            </div>
            ${this._dataComplementoIEDU
                    ? html`
            <div
                class="btn-close"
                .innerHTML=${ic_close}
                @click=${this._required ? null : this.OnCancelComplemento}>
            </div>
            `
                    : null
                }
            <wc-tooltip class="complemento-iedu-tooltip"></wc-tooltip>
            `;
        }

        // actualiza tooltip cada ves despues de renderizar

        protected updated(changedProperties: Map<string, any>) {
            if (changedProperties.has('_dataComplementoIEDU')) {
                this._isDataValid = ValidaComplementoByReglas(this._dataComplementoIEDU);
                this.updateHasErrorState();
            }
            else if (changedProperties.has('_required')) {
                this.updateHasErrorState();
            }
            else if (changedProperties.has('_isDataValid')) {
                this.updateHasErrorState();
            }

            const observedTooltipConfigs: TooltipElementObservedConfig[] = [];
            if (this.complementoContentElement) {
                observedTooltipConfigs.push({
                    Target: this.complementoContentElement,
                    Text: (() => this._dataComplementoIEDU
                        ? (this._isDataValid
                            ? _L("c_actions.editar")
                            : _L("panelfinanzasedocuenta_factura.compl_invalid_data"))
                        : (this._required
                            ? _L("panelfinanzasedocuenta_factura.compl_required_data")
                            : _L("panelfinanzasedocuenta_factura.compl_add_data"))
                    )()
                });
            }
            if (this.btnCloseElement) {
                observedTooltipConfigs.push({
                    Target: this.btnCloseElement,
                    Text: (() => this._required
                        ? _L("panelfinanzasedocuenta_factura.compl_close_disabled_info")
                        : _L("panelfinanzasedocuenta_factura.compl_close_info")
                    )()
                });
            }
            this.tooltipElement._SetObservedElementsAdvanced(...observedTooltipConfigs);
        }


        // PUBLIC METHODS *********************************************************

        public getErrorMessage(): string | null {
            return this._dataComplementoIEDU
                ? (this._isDataValid
                    ? null
                    : _L("panelfinanzasedocuenta_factura.compl_invalid_data"))
                : (this._required
                    ? _L("panelfinanzasedocuenta_factura.compl_required_data")
                    : null);
        }

        // PRIVATE METHODS ********************************************************

        private async OpenComplementoIEDUModalForm() {
            if (!this.modalForm) {
                this.form = await CreateForm(this._idAlumno, this._dataComplementoIEDU, this._required);
                this.modalForm = CreateModalForm({
                    Form: this.form,
                    OnChange: () => {
                        this._dataComplementoIEDU = this.form.__GetDataForm();
                    }
                });
            }
            else {
                this.form.__SetReseterData(this._dataComplementoIEDU);
            }
            this.modalForm.Modal._Mostrar();
        }

        private async OnCancelComplemento() {
            this._dataComplementoIEDU = null;
            this.form?.__SetReseterData(null);
        }

        /** Actualiza el estado de error basado en la validez y requerimientos del complemento
         *
         * Usa las propiedades:
         * - this._required
         * - this._dataComplementoIEDU
         * - this._isDataValid
        */
        private updateHasErrorState() {
            this._hasError = (this._required && !this._dataComplementoIEDU) || (this._dataComplementoIEDU && !this._isDataValid); // Dato requerido y no definido, o dato inválido
        }
    }

    function ValidaComplementoByReglas(complemento: IComplementoIEDU, field?: keyof IComplementoIEDU): boolean {
        if (!complemento) return false;
        const fnValide = (key: keyof IComplementoIEDU) => {
            const regla = REGLAS_COMPLEMENTO_IEDU[key as keyof IComplementoIEDU]
            if (regla?.required) {
                const value = complemento[key as keyof IComplementoIEDU]
                if (!value || !value.toString().trim()) {
                    return false;
                }
            }
            if (regla?.minlength) {
                const value = complemento[key as keyof IComplementoIEDU]
                if (value && value.toString().length < regla.minlength) {
                    return false;
                }
            }
            if (regla?.maxlength) {
                const value = complemento[key as keyof IComplementoIEDU]
                if (value && value.toString().length > regla.maxlength) {
                    return false;
                }
            }
            if (regla?.fnExtraValide) {
                const value = complemento[key as keyof IComplementoIEDU]
                if (value && !regla.fnExtraValide(value.toString())) {
                    return false;
                }
            }
            return true;
        }
        if (field) {
            return fnValide(field);
        }
        for (const key in REGLAS_COMPLEMENTO_IEDU) {
            if (!fnValide(key as keyof IComplementoIEDU)) {
                return false;
            }
        }
        return true;
    }

    // ****************************************************************************************
    // MODAL FORM
    // ****************************************************************************************

    interface ModalFormConfig {
        Form: FormComplemento
        OnChange: () => void
    }
    interface ModalController extends IModalFormController<IComplementoIEDU> {
        Form: FormComplemento
    }
    function CreateModalForm({ Form, OnChange }: ModalFormConfig): ModalController {
        const modalForm = _ModalForm<IComplementoIEDU>({
            Title: _L("panelfinanzasedocuenta_factura.complemento_ins_educ"),
            Width: 550,
            GetForm: () => Form,
            AutoShow: false,
            OnClose: () => { // Cancelar
                Form.__ResetForm() // Resetear formulario a valores anteriores
            },
            OnAccept: () => {
                Form.__SetReseterData(Form._Data)
                modalForm.Modal._Ocultar(false) // No se invocará `OnClose`, OnClose funcionará como cancelar
                OnChange()
                return null
            },
        })

        return modalForm
    }

    // ****************************************************************************************
    // FORM
    // ****************************************************************************************

    type FormComplemento = FormGenerator<IComplementoIEDU> & {
        /** @deprecated */
        _Data: any
        __GetDataForm?(): IComplementoIEDU
        __ResetForm?(): void
        __SetReseterData?(dataForm: IComplementoIEDU): Promise<void>
    }
    async function CreateForm(idAlumno: number, reseterDataFormBase: IComplementoIEDU, edicionLimitada: boolean): Promise<FormComplemento> {
        reseterDataFormBase = reseterDataFormBase || (await util._CreateComplementoIEDUByAlumnoID(idAlumno));
        /** Algunos valores iniciales no se reflejan en el formulario después de volver a asignar datos al formulario */
        const fnFieldDisabled = (model: keyof IComplementoIEDU) => {
            const value = reseterDataFormBase[model];
            let disabled = !value || edicionLimitada && ValidaComplementoByReglas(reseterDataFormBase, model);
            return { disabled, removeBorder: disabled };
        };

        const reglas = REGLAS_COMPLEMENTO_IEDU;
        const formBase = new FormGenerator<IComplementoIEDU>()
            ._Crear({
                OnLoad: (container) => {
                    if (!reseterDataFormBase.NivelEducativo?.trim() || !reseterDataFormBase.AutRVOE?.trim()) {
                        container.append("span").text("Información faltante").style("color", "var(--color_app_red1)")
                    }
                },
                schema: [
                    {
                        model: "NombreAlumno",
                        type: "input",
                        labelText: _L("panelfinanzasedocuenta_factura.compl_nombrealumno"),
                        textInfo: _L("panelfinanzasedocuenta_factura.compl_nombrealumno_info"),
                        inputAttr: { required: reglas.NombreAlumno?.required || false, ...fnFieldDisabled("NombreAlumno") }
                    },
                    {
                        model: "CURP",
                        type: "input",
                        labelText: _L("panelfinanzasedocuenta_factura.compl_curp"),
                        onGetFixedValue: (value: string) => (value || "").toUpperCase(),
                        onValidate: (value) => UIUtilGeneral._ValidarCURP(value) || _L("general.curp_invalid"),
                        textInfo: _L("panelfinanzasedocuenta_factura.compl_curp_info"),
                        inputAttr: {
                            required: reglas.CURP?.required || false, minlength: reglas.CURP?.minlength, maxlength: reglas.CURP?.maxlength,
                            ...fnFieldDisabled("CURP"),
                            onload: (input) => input.style("text-transform", "uppercase") // UIUtilMask._ApplyUpperCaseMask(input, false, true, false)
                        }
                    },
                    {
                        model: "NivelEducativo",
                        type: "input",
                        labelText: _L("panelfinanzasedocuenta_factura.compl_nivel"),
                        textInfo: _L("panelfinanzasedocuenta_factura.compl_nivel_info"),
                        inputAttr: { required: reglas.NivelEducativo?.required || false, ...fnFieldDisabled("NivelEducativo") }
                    },
                    {
                        model: "AutRVOE",
                        type: "input",
                        labelText: _L("panelfinanzasedocuenta_factura.compl_autorizacion_rvoe"),
                        textInfo: _L("panelfinanzasedocuenta_factura.compl_autorizacion_rvoe_info"),
                        inputAttr: { required: reglas.AutRVOE?.required || false, ...fnFieldDisabled("AutRVOE") }
                    },
                    {
                        model: "RFCPago",
                        type: "input",
                        labelText: _L("panelfinanzasedocuenta_factura.compl_rfcpago"),
                        textInfo: _L("panelfinanzasedocuenta_factura.compl_rfcpago_info"),
                        onGetFixedValue: (value: string) => (value || "").toUpperCase(),
                        onValidate: (value) => UIUtilGeneral._ValidarRFC(value) || _L("general.rfc_invalid"),
                        inputAttr: {
                            required: reglas.RFCPago?.required || false,
                            minlength: reglas.RFCPago?.minlength, maxlength: reglas.RFCPago?.maxlength,
                            onload: (input) => input.style("text-transform", "uppercase")
                        }
                    }
                ]
            })

        const formFinal: FormComplemento = formBase;

        formBase._AsignaData(reseterDataFormBase as IComplementoIEDU);
        setTimeout(() => formFinal._GetIsValidForm()); // Forzar validación para actualizar estilos

        formFinal.__SetReseterData = async (reseterData) => {
            reseterDataFormBase = reseterData || (await util._CreateComplementoIEDUByAlumnoID(idAlumno));
            formFinal._AsignaData(reseterDataFormBase);
            setTimeout(() => formFinal._GetIsValidForm()); // Forzar validación para actualizar estilos
        }
        formFinal.__ResetForm = () => {
            formFinal._AsignaData(reseterDataFormBase as any);
        }
        formFinal.__GetDataForm = () => {
            if (!formFinal._GetIsValidForm()) {
                NotificacionV2._Mostrar(_L("general.notif_fail"), "ADVERTENCIA")
                throw new Error("CFDI Complemento inválido")
            }
            return formBase._Data
        }

        return formFinal
    }
}